/**
 * Camera-fixed HUD: level, words typed, accuracy, a climb progress bar, and the
 * always-visible mute plus pause buttons (CLAUDE.md).
 *
 * In Tide Run the two stat lines become the run clock with the levels cleared,
 * and how far below the water is; the progress bar fills with water too.
 */
import Phaser from 'phaser';
import { IconButton } from '../ui/IconButton';
import { PAUSE_DEPTH } from './PauseMenu';
import { formatClock } from '../../systems/TideRun';
import { FONT_FAMILY, GAME_HEIGHT, GAME_WIDTH, PALETTE } from '../../types';

export const HUD_DEPTH = 100;

const BAR_X = GAME_WIDTH - 44;
const BAR_TOP = 150;
const BAR_HEIGHT = 420;
const BAR_WIDTH = 18;

export interface HudHandlers {
  onPause(): void;
  onToggleMute(): void;
}

export class Hud {
  private readonly container: Phaser.GameObjects.Container;
  private readonly levelText: Phaser.GameObjects.Text;
  private readonly statsText: Phaser.GameObjects.Text;
  private readonly scoreText: Phaser.GameObjects.Text;
  private readonly progressText: Phaser.GameObjects.Text;
  private readonly bar: Phaser.GameObjects.Graphics;
  private readonly muteButton: IconButton;
  private readonly pauseButton: IconButton;
  private progressIndex = 0;
  /** Tide Run: the water's height in platforms, or null outside a run. */
  private waterPosition: number | null = null;

  constructor(
    scene: Phaser.Scene,
    private readonly level: number,
    private readonly platformCount: number,
    handlers: HudHandlers,
    private readonly tide = false,
  ) {
    this.levelText = scene.add.text(28, 22, `Level ${level}`, {
      fontFamily: FONT_FAMILY,
      fontSize: '34px',
      color: PALETTE.snow,
      stroke: PALETTE.ink,
      strokeThickness: 6,
    });

    this.statsText = scene.add.text(28, 66, '', {
      fontFamily: FONT_FAMILY,
      fontSize: '22px',
      color: PALETTE.ice,
      stroke: PALETTE.ink,
      strokeThickness: 4,
    });

    this.scoreText = scene.add.text(28, 100, '', {
      fontFamily: FONT_FAMILY,
      fontSize: '26px',
      color: PALETTE.gold,
      stroke: PALETTE.ink,
      strokeThickness: 5,
    });

    this.bar = scene.add.graphics();

    this.progressText = scene.add
      .text(BAR_X, BAR_TOP - 34, `0 / ${platformCount - 1}`, {
        fontFamily: FONT_FAMILY,
        fontSize: '20px',
        color: PALETTE.snow,
        stroke: PALETTE.ink,
        strokeThickness: 4,
      })
      .setOrigin(0.5, 0.5);

    // Icons rather than words: a speaker for sound, two bars for pause. The
    // pause button sits above the pause menu, where it shows play and resumes.
    this.muteButton = new IconButton(scene, GAME_WIDTH - 98, 38, 'sound', handlers.onToggleMute)
      .setScrollFactor(0)
      .setDepth(HUD_DEPTH + 1);
    this.pauseButton = new IconButton(scene, GAME_WIDTH - 40, 38, 'pause', handlers.onPause)
      .setScrollFactor(0)
      .setDepth(PAUSE_DEPTH + 10);

    this.container = scene.add
      .container(0, 0, [
        this.levelText,
        this.statsText,
        this.scoreText,
        this.bar,
        this.progressText,
      ])
      .setDepth(HUD_DEPTH)
      .setScrollFactor(0);

    this.setProgress(0);
    if (tide) {
      this.setTideClock(0, 0);
      this.setWater(-2, 2, false);
    } else {
      this.setStats(0, 100);
      this.setScore(0, 1);
    }
  }

  setProgress(index: number): void {
    this.progressIndex = index;
    this.drawBar();
  }

  /** Tide Run: the run clock and how many levels this run has cleared. */
  setTideClock(runMs: number, cleared: number): void {
    this.statsText.setText(`Time ${formatClock(runMs)}    Cleared ${cleared}`);
  }

  /** Tide Run: the water's height and the platforms of margin above it. */
  setWater(position: number, margin: number, danger: boolean): void {
    // Half-platform steps: a count that flickers every frame is noise.
    const below = Math.max(0, Math.floor(margin * 2) / 2);
    this.scoreText.setText(below < 0.5 ? 'Water close!' : `Water ${below} below`);
    this.scoreText.setColor(danger ? PALETTE.sunset : PALETTE.ice);
    // Redrawn only when the water crosses a visible step of the bar.
    const step = Math.round(position * 8) / 8;
    if (step !== this.waterPosition) {
      this.waterPosition = step;
      this.drawBar();
    }
  }

  private drawBar(): void {
    const index = this.progressIndex;
    const top = this.platformCount - 1;
    const ratio = top <= 0 ? 0 : Math.min(1, Math.max(0, index / top));
    this.progressText.setText(`${index} / ${top}`);

    this.bar.clear();
    this.bar.fillStyle(0x0b1220, 0.6);
    this.bar.fillRoundedRect(BAR_X - BAR_WIDTH / 2, BAR_TOP, BAR_WIDTH, BAR_HEIGHT, 9);
    this.bar.lineStyle(3, 0x8ecae6, 1);
    this.bar.strokeRoundedRect(BAR_X - BAR_WIDTH / 2, BAR_TOP, BAR_WIDTH, BAR_HEIGHT, 9);

    // Fills upward, so the bar reads like the climb itself.
    const fill = Math.max(4, BAR_HEIGHT * ratio);
    this.bar.fillStyle(0x2ec4b6, 1);
    this.bar.fillRoundedRect(
      BAR_X - BAR_WIDTH / 2 + 4,
      BAR_TOP + BAR_HEIGHT - fill + 4,
      BAR_WIDTH - 8,
      Math.max(2, fill - 8),
      5,
    );

    // Tide Run: the sea, rising up the same bar toward the climber.
    if (this.waterPosition !== null && top > 0 && this.waterPosition > 0) {
      const water = BAR_HEIGHT * Math.min(1, this.waterPosition / top);
      this.bar.fillStyle(0x1f78c8, 0.9);
      this.bar.fillRoundedRect(
        BAR_X - BAR_WIDTH / 2 + 2,
        BAR_TOP + BAR_HEIGHT - water,
        BAR_WIDTH - 4,
        Math.max(4, water - 2),
        5,
      );
    }

    // Goal marker.
    this.bar.fillStyle(0xffc145, 1);
    this.bar.fillCircle(BAR_X, BAR_TOP - 2, 6);
  }

  setStats(wordsTyped: number, accuracyPercent: number): void {
    if (this.tide) return;
    this.statsText.setText(`Words ${wordsTyped}    Accuracy ${Math.round(accuracyPercent)}%`);
  }

  /** Score plus the live streak multiplier, so the x2 feels earned. */
  setScore(score: number, multiplier: number): void {
    if (this.tide) return;
    const suffix = multiplier > 1 ? `    x${multiplier}` : '';
    this.scoreText.setText(`Score ${score}${suffix}`);
    this.scoreText.setColor(multiplier > 1 ? PALETTE.sunset : PALETTE.gold);
  }

  setMuted(muted: boolean): void {
    this.muteButton.setIcon(muted ? 'muted' : 'sound');
  }

  /** Pause shows two bars; while paused it shows play, to resume. */
  setPaused(paused: boolean): void {
    this.pauseButton.setIcon(paused ? 'play' : 'pause');
  }

  destroy(): void {
    this.container.destroy();
    this.muteButton.destroy();
    this.pauseButton.destroy();
  }

  /** Level is kept for Prompt 6, which shows par time beside it. */
  get levelNumber(): number {
    return this.level;
  }
}

/** Shared y for anything else pinned to the bottom of the HUD later. */
export const HUD_BOTTOM_Y = GAME_HEIGHT - 28;
